const dvaliaHydratedGalleries = new WeakSet();
function dvaliaGalleryShow(gallery, requestedIndex, scroll = true) {
  const track = gallery.querySelector('[data-dv-gallery-track]');
  if (!track) return;
  const slides = [...track.querySelectorAll('[data-dv-photo]')].filter(slide => !slide.hidden);
  const index = Math.max(0, Math.min(Number(requestedIndex) || 0, slides.length - 1));
  gallery.dataset.dvGalleryIndex = String(index);
  gallery.querySelectorAll('[data-dv-gallery-slide]').forEach(button => {
    const position = Number(button.dataset.dvGallerySlide);
    button.hidden = position >= slides.length;
    button.setAttribute('aria-pressed', String(position === index));
  });
  gallery.querySelectorAll('[data-dv-gallery-step]').forEach(button => {
    button.hidden = slides.length < 2;
    button.disabled = slides.length < 2;
  });
  const controls = gallery.querySelector('[data-dv-gallery-controls]');
  if (controls) controls.hidden = slides.length < 2;
  const status = gallery.querySelector('[data-dv-gallery-status]');
  const message = status?.dataset.dvStatusTemplate?.replace('__current__', String(index + 1)).replace('__total__', String(slides.length));
  if (status && message && status.textContent !== message) status.textContent = message;
  if (scroll) track.scrollTo({ left: index * track.clientWidth, behavior: 'auto' });
}
function hydrateDvaliaGalleries(root = document) {
  root.querySelectorAll('[data-dv-gallery]').forEach(gallery => {
    if (dvaliaHydratedGalleries.has(gallery)) return;
    const track = gallery.querySelector('[data-dv-gallery-track]');
    if (!track) return;
    dvaliaHydratedGalleries.add(gallery);
    let drag = null;
    gallery.addEventListener('click', event => {
      const step = event.target.closest('[data-dv-gallery-step]');
      const dot = event.target.closest('[data-dv-gallery-slide]');
      if (step) {
        const count = [...track.querySelectorAll('[data-dv-photo]')].filter(slide => !slide.hidden).length;
        dvaliaGalleryShow(gallery, (Number(gallery.dataset.dvGalleryIndex) + Number(step.dataset.dvGalleryStep) + count) % count);
      }
      if (dot) dvaliaGalleryShow(gallery, Number(dot.dataset.dvGallerySlide));
    });
    track.addEventListener('scroll', () => {
      if (!drag && track.clientWidth) dvaliaGalleryShow(gallery, Math.round(track.scrollLeft / track.clientWidth), false);
    }, { passive: true });
    track.addEventListener('keydown', event => {
      if (event.target !== track) return;
      const index = Number(gallery.dataset.dvGalleryIndex);
      const target = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: track.querySelectorAll('[data-dv-photo]').length - 1 }[event.key];
      if (target === undefined) return;
      event.preventDefault();
      dvaliaGalleryShow(gallery, target);
    });
    track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, left: track.scrollLeft, id: event.pointerId };
      track.dataset.dvDragging = '';
      track.setPointerCapture(event.pointerId);
      track.focus({ preventScroll: true });
    });
    track.addEventListener('pointermove', event => {
      if (!drag) return;
      track.scrollLeft = drag.left + drag.x - event.clientX;
    });
    const finishDrag = () => {
      if (!drag) return;
      const pointerId = drag.id;
      const index = Math.round(track.scrollLeft / track.clientWidth);
      drag = null;
      delete track.dataset.dvDragging;
      if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
      dvaliaGalleryShow(gallery, index);
    };
    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);
    dvaliaGalleryShow(gallery, Number(gallery.dataset.dvGalleryIndex));
  });
}
const dvaliaPillowcaseSizes = new Set(['50x70', '50x60', '60x70', '50x75', '60x60', '65x65']);
function dvaliaRecommendedSize(country) {
  return { NO: '50x70', GB: '50x75', AU: '50x75' }[country] || '';
}
function dvaliaPricingCountry(country, currency, marketId) {
  return String(marketId || '').split('/').pop() === '105904800051' && currency === 'NOK' ? 'NO' : country;
}
function dvaliaVariantFor(variants, colour, size) {
  if (!dvaliaPillowcaseSizes.has(size)) return undefined;
  return variants.find(variant => variant.colour === colour && variant.size === size && variant.available && variant.stock > 0);
}
function dvaliaAvailabilityLabel(variants, colour, size, quantity) {
  const variant = variants.find(item => item.colour === colour && item.size === size);
  if (!variant) return 'unavailable';
  if (variant.stock <= 0) return 'soldOut';
  if (!variant.available) return 'unavailable';
  return variant.stock < quantity ? 'insufficientStock' : '';
}
function dvaliaSelections(states, quantity, mixed) {
  if (quantity === 1) return [states[0]];
  return mixed ? states.slice(0, 2) : [states[0], states[0]];
}
function dvaliaCartItems(variants, selections) {
  if (!selections.length || selections.length > 2) return null;
  const items = new Map();
  for (const selection of selections) {
    const variant = dvaliaVariantFor(variants, selection.colour, selection.size);
    if (!variant || !Number.isSafeInteger(Number(variant.id))) return null;
    const existing = items.get(String(variant.id));
    const quantity = (existing?.quantity || 0) + 1;
    if (quantity > variant.stock) return null;
    items.set(String(variant.id), { id: Number(variant.id), quantity });
  }
  return [...items.values()];
}
function dvaliaHalloweenQuote(selected, country, currency, config, now) {
  if (!config?.enabled || !Number.isFinite(now) || now >= Date.parse(config.endsAt)) return null;
  const allowed = { NOK: ['NO'], GBP: ['GB'], USD: ['US','CA','AU','SE','DK'], EUR: ['AT','BE','BG','HR','EE','FI','FR','DE','GR','IE','IT','LT','MT','NL','PT','SK','SI','ES'] };
  if (!allowed[currency]?.includes(country)) return null;
  const expected = { '50x70':64900,'50x60':64900,'60x60':66900,'50x75':68900,'60x70':70900,'65x65':70900 };
  const regular = { USD:6999, GBP:5299, EUR:6299 };
  const local = { USD:[5999,9199],GBP:[4499,6899],EUR:[5399,8299] };
  if (currency === 'NOK') {
    if (!selected.every(v => expected[v.size] === v.price)) return null;
    return selected.reduce((sum,v) => sum + v.price,0) - (selected.length === 2 ? 44900 : 10000);
  }
  if (!selected.every(v => v.price === regular[currency])) return null;
  return local[currency]?.[selected.length - 1] ?? null;
}
function dvaliaOfferPrices(variants, selections, country, currency, eventConfig = null, now = Date.now()) {
  if (!selections.length || selections.length > 2) return null;
  const selected = selections.map(selection => dvaliaVariantFor(variants, selection.colour, selection.size));
  if (selected.some(variant => !variant || !Number.isSafeInteger(variant.price) || variant.price < 0)) return null;
  const eventTotal = dvaliaHalloweenQuote(selected, country, currency, eventConfig, now);
  if (Number.isSafeInteger(eventTotal)) return { total: eventTotal, saving: 0, campaign: true };
  const total = selected.reduce((sum, variant) => sum + variant.price, 0);
  const norwayPrices = { '50x70': 64900, '50x60': 64900, '60x60': 66900, '50x75': 68900, '60x70': 70900, '65x65': 70900 };
  if (country === 'NO' && currency === 'NOK' && selected.every(variant => variant.price === norwayPrices[variant.size])) {
    const saving = selected.length === 2 ? 39900 : 0;
    return { total: total - saving, saving };
  }
  const candidates = {
    NOK: [{ countries: ['NO'], single: 44900, pair: 59900 }],
    USD: [
      { countries: ['US', 'CA', 'AU', 'SE', 'DK'], single: 4499, pair: 5999 },
      { countries: ['US', 'CA', 'AU', 'SE', 'DK'], single: 6999, pair: 9699 },
    ],
    GBP: [
      { countries: ['GB'], single: 3499, pair: 4499 },
      { countries: ['GB'], single: 5299, pair: 7299 },
    ],
    EUR: [
      { countries: ['AT', 'BE', 'BG', 'HR', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LT', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES'], single: 3999, pair: 5499 },
      { countries: ['AT', 'BE', 'BG', 'HR', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LT', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES'], single: 6299, pair: 8699 },
    ],
  }[currency] || [];
  const verified = candidates.find(candidate => candidate.countries.includes(country) && selected.every(variant => variant.price === candidate.single));
  const eligible = Boolean(verified) && selected.length === 2;
  return { total: eligible ? verified.pair : total, saving: eligible ? total - verified.pair : 0 };
}
const dvaliaHydratedOffers = new WeakSet();
function hydrateDvaliaOffers(root = document) {
  const offers = Array.from(root.querySelectorAll('[data-dv-offer]'));
  if (root.matches && root.matches('[data-dv-offer]')) offers.unshift(root);
  offers.forEach(offer => {
    if (dvaliaHydratedOffers.has(offer)) return;
    let data;
    let labels;
    let eventConfig = null;
    try {
      data = JSON.parse(offer.querySelector('[data-dv-variants]').textContent);
      labels = JSON.parse(offer.querySelector('[data-dv-selection-labels]').textContent);
      eventConfig = JSON.parse(offer.querySelector('[data-dv-event-config]')?.textContent || 'null');
    } catch { return; }
    data.variants = data.variants.filter(variant => dvaliaPillowcaseSizes.has(variant.size));
    const variants = data.variants.filter(variant => variant.available && variant.stock > 0);
    if (!variants.length) return;
    dvaliaHydratedOffers.add(offer);
    const panels = [...offer.querySelectorAll('[data-dv-unit]')];
    const status = offer.querySelector('[data-dv-selection-status]');
    const quantityInput = offer.querySelector('[data-dv-quantity]');
    const cartFields = offer.querySelector('[data-dv-cart-fields]');
    const mixToggle = offer.querySelector('[data-dv-mix-toggle]');
    const mixControl = offer.querySelector('[data-dv-mix-control]');
    const submitButton = offer.querySelector('[data-dv-submit]');
    const pairDetails = offer.querySelector('[data-dv-pair-details]');
    const bonusCheckbox = offer.querySelector('[data-dv-pair-mask-add]');
    const bonusChoice = offer.querySelector('[data-dv-pair-mask-choice]');
    let bonusData = null;
    try { bonusData = JSON.parse(offer.querySelector('[data-dv-pair-mask-data]')?.textContent || 'null'); } catch {}
    if (bonusCheckbox) bonusCheckbox.checked = false;

    const checkoutButtons = [...document.querySelectorAll('[data-dv-checkout]')].filter(button => button.form === offer);
    let submitting = false;
    const country = offer.dataset.dvCountry;
    const pricingCountry = dvaliaPricingCountry(country, data.currency, offer.dataset.dvMarket);
    const localSize = dvaliaRecommendedSize(country);
    const variantParameters = new URLSearchParams(window.location.search);
    const linkedVariant = data.variants.find(variant => String(variant.id) === variantParameters.get('variant'));
    const first = linkedVariant || variants.find(variant => localSize && variant.size === localSize) || variants.find(variant => variant.size === '50x70') || variants[0];
    if (linkedVariant && variantParameters.get('pack') === '1') {
      const singleOption = offer.querySelector('input[type="radio"][data-price][value="1"]');
      if (singleOption) singleOption.checked = true;
      quantityInput.value = '1';
    }
    const states = panels.map(() => ({ colour: first.colour, size: first.size, allColoursVisible: false }));
    let activePanel = 0;
    let imageColour = '';
    let colourReveal = 0;
    const gallery = offer.closest('.dv-product')?.querySelector('.dv-gallery');
    const image = gallery?.querySelector('[data-dv-photo="full"] img');
    const foldedImage = gallery?.querySelector('[data-dv-photo="folded"] img');
    const lifestyleImage = gallery?.querySelector('[data-dv-photo="lifestyle"] img');
    let galleryImages = {};
    try { galleryImages = JSON.parse(gallery?.querySelector('[data-dv-gallery-data]')?.textContent || '{}'); } catch {}

    const formatSize = value => value.replace('x', ' × ') + ' cm';
    const currencyDigits = new Intl.NumberFormat('en', { style: 'currency', currency: data.currency }).resolvedOptions().maximumFractionDigits;
    const formatMoney = (value, perUnit = false) => Number.isFinite(value) ? new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: data.currency, currencyDisplay: 'symbol', minimumFractionDigits: perUnit ? currencyDigits : 0, maximumFractionDigits: currencyDigits }).format(value / 100) : labels.pricingUnavailable;
    const colours = [...new Set(variants.map(variant => variant.colour))];
    if (!colours.includes(first.colour)) colours.push(first.colour);
    const featured = ['Taupe', 'White', 'Silver', 'Dustypink', 'Dark Grey', 'Black'].filter(value => colours.includes(value));
    const orderedColours = [...featured, ...colours.filter(value => !featured.includes(value))];
    const visibleCount = Math.min(6, orderedColours.length);
    const featuredColours = orderedColours.slice(0, visibleCount);
    const sizes = [...new Set(variants.map(variant => variant.size))].sort((a, b) => {
      const x = a.split('x').map(Number);
      const y = b.split('x').map(Number);
      return x[0] - y[0] || x[1] - y[1];
    });
    if (!sizes.includes(first.size)) sizes.push(first.size);
    const featuredSizes = ['50x70', '50x75', '50x90'].filter(value => sizes.includes(value));
    for (const value of sizes) {
      if (featuredSizes.length === 3) break;
      if (!featuredSizes.includes(value)) featuredSizes.push(value);
    }
    const controls = panels.map((panel, panelIndex) => {
      const state = states[panelIndex];
      const colourOptions = panel.querySelector('[data-dv-colour-options]');
      const colourToggle = panel.querySelector('[data-dv-colour-toggle]');
      const colourName = panel.querySelector('[data-dv-colour-name]');
      const mainSizes = panel.querySelector('[data-dv-main-sizes]');
      const extraSizes = panel.querySelector('[data-dv-extra-sizes]');
      const otherSizes = panel.querySelector('[data-dv-other-sizes]');
      colourOptions.replaceChildren(...orderedColours.map(value => {
        const option = document.createElement('label');
        option.className = 'dv-colour-option';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'colour-' + offer.id + '-' + panelIndex;
        input.value = value;
        input.className = 'dv-sr';
        input.dataset.dvColour = '';
        const card = document.createElement('span');
        card.className = 'dv-colour-card';
        const swatch = document.createElement('span');
        swatch.className = 'dv-colour-swatch';
        swatch.setAttribute('aria-hidden', 'true');
        const thumbnail = document.createElement('img');
        const source = variants.find(variant => variant.colour === value && variant.image)?.image;
        if (source) {
          const thumbnailUrl = new URL(source, window.location.origin);
          thumbnailUrl.searchParams.set('width', '200');
          thumbnail.src = thumbnailUrl.href;
        }
        thumbnail.alt = '';
        thumbnail.width = 100;
        thumbnail.height = 100;
        thumbnail.loading = 'lazy';
        thumbnail.decoding = 'async';
        swatch.append(thumbnail);
        const name = document.createElement('span');
        name.className = 'dv-colour-name';
        name.textContent = labels.colours[value] || value;
        card.append(swatch, name);
        option.append(input, card);
        return option;
      }));
      const sizeCards = new Map(sizes.map(value => {
        const option = document.createElement('label');
        option.className = 'dv-size-option';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'size-' + offer.id + '-' + panelIndex;
        input.value = value;
        input.className = 'dv-sr';
        input.dataset.dvSize = '';
        const card = document.createElement('span');
        card.className = 'dv-size-card';
        const name = document.createElement('span');
        name.className = 'dv-size-name';
        const dimensions = document.createElement('span');
        dimensions.className = 'dv-size-dimensions';
        dimensions.textContent = formatSize(value);
        const availability = document.createElement('span');
        availability.className = 'dv-size-availability';
        availability.textContent = labels.sizeNames.unavailable;
        card.append(name, dimensions, availability);
        option.append(input, card);
        return [value, option];
      }));
      colourToggle.addEventListener('click', () => {
        state.allColoursVisible = !state.allColoursVisible;
        render();
      });
      otherSizes.addEventListener('toggle', () => { render(); });
      return { panel, colourOptions, colourToggle, colourName, mainSizes, extraSizes, otherSizes, sizeCards };
    });
    function quantityFor(variant, panelIndex, quantity) {
      if (quantity !== 2 || !variant) return 1;
      if (!mixToggle.checked) return 2;
      const other = states[1 - panelIndex];
      const otherVariant = dvaliaVariantFor(variants, other.colour, other.size);
      return otherVariant?.id === variant.id ? 2 : 1;
    }
    function renderPanel(control, panelIndex, quantity) {
      const { panel, colourOptions, colourToggle, colourName, mainSizes, extraSizes, otherSizes, sizeCards } = control;
      const state = states[panelIndex];
      const mixed = quantity === 2 && mixToggle.checked;
      panel.hidden = panelIndex > 0 && !mixed;
      panel.querySelector('[data-dv-unit-heading]').hidden = !mixed;
      const visibleColours = [...featuredColours];
      if (!state.allColoursVisible && !visibleColours.includes(state.colour)) visibleColours[visibleColours.length - 1] = state.colour;
      colourOptions.querySelectorAll('[data-dv-colour]').forEach(input => {
        input.checked = input.value === state.colour;
        input.closest('.dv-colour-option').hidden = !state.allColoursVisible && !visibleColours.includes(input.value);
      });
      colourName.textContent = labels.colours[state.colour] || state.colour;
      colourToggle.hidden = colours.length <= visibleCount;
      colourToggle.setAttribute('aria-expanded', String(state.allColoursVisible));
      colourToggle.textContent = state.allColoursVisible ? labels.fewerColours : labels.viewAllColours;
      const shownSizes = [...featuredSizes];
      if (!otherSizes.open && state.size && !shownSizes.includes(state.size)) shownSizes[shownSizes.length - 1] = state.size;
      const remainingSizes = sizes.filter(value => !shownSizes.includes(value));
      [[mainSizes, shownSizes], [extraSizes, remainingSizes]].forEach(([container, values]) => {
        values.forEach((value, index) => {
          const option = sizeCards.get(value);
          const current = container.children[index];
          if (current !== option) container.insertBefore(option, current || null);
        });
      });
      let extraCount = 0;
      sizeCards.forEach((option, value) => {
        const input = option.querySelector('input');
        const variant = dvaliaVariantFor(variants, state.colour, value);
        const available = Boolean(variant && variant.stock >= quantityFor(variant, panelIndex, quantity));
        const isExtra = !shownSizes.includes(value);
        option.hidden = isExtra && !variant;
        if (isExtra && variant) extraCount += 1;
        input.checked = value === state.size;
        input.disabled = !available;
        const [width, length] = value.split('x');
        const shortName = { '50x70': labels.sizeNames.classic, '50x75': labels.sizeNames.long, '50x90': labels.sizeNames.extraLong }[value];
        option.querySelector('.dv-size-name').textContent = value === localSize ? labels.standard : shortName || (width === length ? labels.sizeNames.square : labels.sizeNames.rectangle);
        const availability = option.querySelector('.dv-size-availability');
        const availabilityLabel = dvaliaAvailabilityLabel(data.variants, state.colour, value, quantityFor(variant, panelIndex, quantity));
        availability.textContent = labels.sizeNames[availabilityLabel] || labels.sizeNames.unavailable;
        availability.hidden = available;
      });
      otherSizes.hidden = extraCount === 0;
    }
    function renderImage(variant) {
      if (!image || !variant || imageColour === variant.colour) return;
      imageColour = variant.colour;
      const media = galleryImages[variant.colour] || {};
      const colourLabel = labels.colours[variant.colour] || variant.colour;
      const activeIndex = Number(gallery.dataset.dvGalleryIndex) || 0;
      const mainSource = media.main || variant.image;
      if (mainSource) {
        image.removeAttribute('srcset');
        image.src = mainSource;
        image.alt = (gallery.dataset.dvMainAlt || '__colour__ Mulberry silk pillowcase by DVALIA').replace('__colour__', colourLabel);
      }
      const mainFrame = image.closest('[data-dv-photo]');
      mainFrame.style.setProperty('--dv-photo-scale', String(media.scale || 1));
      mainFrame.style.setProperty('--dv-photo-x', String(media.x || 0) + '%');
      mainFrame.style.setProperty('--dv-photo-y', String(media.y || 0) + '%');
      if (foldedImage && media.folded) {
        foldedImage.removeAttribute('srcset');
        foldedImage.src = media.folded;
        const template = gallery.querySelector('[data-dv-folded-alt]')?.dataset.dvFoldedAlt || '__colour__ · DVALIA';
        foldedImage.alt = template.replace('__colour__', colourLabel);
        const foldedFrame = foldedImage.closest('[data-dv-photo]');
        foldedFrame.style.setProperty('--dv-photo-scale', String(media.foldedScale || 1));
        foldedFrame.style.setProperty('--dv-photo-x', String(media.foldedX || 0) + '%');
        foldedFrame.style.setProperty('--dv-photo-y', String(media.foldedY || 0) + '%');
      }
      if (foldedImage) foldedImage.closest('[data-dv-photo]').hidden = !media.folded;
      if (lifestyleImage) {
        if (media.lifestyle) {
          lifestyleImage.removeAttribute('srcset');
          lifestyleImage.src = media.lifestyle;
          const template = gallery.querySelector('[data-dv-lifestyle-alt]')?.dataset.dvLifestyleAlt || '__colour__ · DVALIA';
          lifestyleImage.alt = template.replace('__colour__', colourLabel);
        }
        lifestyleImage.closest('[data-dv-photo]').hidden = !media.lifestyle;
      }
      gallery.dataset.dvColour = variant.colour;
      dvaliaGalleryShow(gallery, activeIndex);
    }
    async function revealColourImage(request) {
      const mobile = window.matchMedia('(max-width: 760px)');
      if (!mobile.matches || !gallery || !image) return;
      const source = image.src;
      dvaliaGalleryShow(gallery, 0);
      try { await image.decode(); } catch { return; }
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (request !== colourReveal || !mobile.matches || !gallery.isConnected || image.src !== source || !image.naturalWidth) return;
      const header = document.querySelector('.dv-header');
      const pinnedHeader = header && ['sticky', 'fixed'].includes(getComputedStyle(header).position);
      const clearance = (pinnedHeader ? Math.max(0, header.getBoundingClientRect().bottom) : 0) + 16;
      const top = Math.max(0, window.scrollY + gallery.getBoundingClientRect().top - clearance);
      if (top >= window.scrollY - 1) return;
      window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    function render() {
      const checked = offer.querySelector('input[type="radio"][data-price]:checked');
      const quantity = Number(checked?.value || 2);
      const mixed = quantity === 2 && mixToggle.checked;
      const selections = dvaliaSelections(states, quantity, mixed);
      controls.forEach((control, index) => { renderPanel(control, index, quantity); });
      const items = dvaliaCartItems(variants, selections);
      quantityInput.value = String(quantity);
      cartFields.replaceChildren();
      (items || []).forEach((item, index) => {
        for (const field of ['id', 'quantity']) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'items[' + index + '][' + field + ']';
          input.value = String(item[field]);
          input.disabled = offer.dataset.dvSalesLocked !== 'false' || offer.dataset.dvPricingVerified !== 'true';
          cartFields.append(input);
        }
      });
      offer.dataset.dvVariantIds = items ? items.map(item => item.id).join(',') : '';
      offer.dataset.dvQuantity = String(quantity);
      offer.dataset.dvStockValid = String(Boolean(items));
      offer.dataset.dvMixEnabled = String(mixed);
      submitButton.disabled = submitting || data.preview === true || !items || offer.dataset.dvSalesLocked !== 'false' || offer.dataset.dvPricingVerified !== 'true';
      status.hidden = Boolean(items);
      status.textContent = items ? '' : labels.unavailable;
      const details = offer.closest('main')?.querySelectorAll('[data-dv-selected-size]') || [];
      details.forEach(detail => {
        const prompt = detail.dataset.dvSizePrompt;
        const detailSelections = mixed ? selections : selections.slice(0, 1);
        detail.textContent = detailSelections.map((selection, index) => (mixed ? (index === 0 ? labels.unitOne : labels.unitTwo) + ': ' : '') + (selection.size ? formatSize(selection.size) : prompt)).join(' · ');
      });
      const single = dvaliaOfferPrices(variants, selections.slice(0, 1), pricingCountry, data.currency, eventConfig);
      const singlePrice = single?.total;
      const pairSelections = dvaliaSelections(states, 2, mixToggle.checked);
      const pair = dvaliaOfferPrices(variants, pairSelections, pricingCountry, data.currency, eventConfig);
      const pairPrice = pair?.total;
      const originalPairPrice = pair ? pair.total + pair.saving : null;
      let selectedPrice = quantity === 2 ? pairPrice : singlePrice;
      let bonusSelected = false;
      let bonusUnavailable = false;
      if (pairDetails) {
        pairDetails.hidden = quantity !== 2;
        const extra = offer.querySelector('[data-dv-pair-extra]');
        const extraPrice = pairPrice - singlePrice;
        extra.hidden = !Number.isFinite(extraPrice) || extraPrice <= 0;
        extra.textContent = labels.pairExtra?.replace('__amount__', formatMoney(extraPrice)) || '';
        if (quantity !== 2 && bonusCheckbox) bonusCheckbox.checked = false;
        if (bonusData && bonusCheckbox && bonusChoice) {
          const mask = bonusData.variants.find(variant => String(variant.id) === bonusChoice.value);
          const available = mask?.available && mask.stock > 0 && Number.isSafeInteger(mask.price) && Number.isSafeInteger(mask.bonusPrice);
          bonusCheckbox.disabled = !available;
          if (!available) bonusCheckbox.checked = false;
          bonusSelected = quantity === 2 && bonusCheckbox.checked && available;
          if (mask) {
            offer.querySelector('[data-dv-pair-mask-price]').textContent = formatMoney(mask.bonusPrice);
            offer.querySelector('[data-dv-pair-mask-separate]').textContent = bonusData.separate.replace('__amount__', formatMoney(mask.price));
            offer.querySelector('[data-dv-pair-mask-label]').textContent = bonusData.add.replace('__amount__', formatMoney(mask.bonusPrice));
            const maskImage = offer.querySelector('[data-dv-pair-mask-image]');
            if (mask.image && maskImage.getAttribute('src') !== mask.image) maskImage.src = mask.image;
            maskImage.alt = bonusData.title + ' – ' + mask.colour;
          }
          if (bonusSelected) selectedPrice += mask.bonusPrice;
          bonusUnavailable = bonusSelected && bonusData.verified !== true;
          const blocked = offer.querySelector('[data-dv-pair-mask-blocked]');
          blocked.hidden = !bonusUnavailable;
        }
        offer.querySelector('[data-dv-pair-total]').textContent = formatMoney(selectedPrice);
      }
      const event = offer.querySelector('[data-dv-event]');
      const campaign = Boolean(single?.campaign);
      offer.dataset.dvEventActive = String(campaign);
      if (event) event.hidden = !campaign;
      const ordinarySingle = dvaliaOfferPrices(variants, selections.slice(0,1), pricingCountry, data.currency);
      const ordinaryPair = dvaliaOfferPrices(variants, pairSelections, pricingCountry, data.currency);
      offer.querySelectorAll('[data-dv-event-later]').forEach(element => {
        const ordinary = element.dataset.dvEventLater === '2' ? ordinaryPair : ordinarySingle;
        const visible = campaign && Number.isFinite(ordinary?.total);
        element.hidden = !visible;
        element.textContent = visible ? labels.eventLaterLabel : '';
        const amount = offer.querySelector('[data-dv-event-price-later="' + element.dataset.dvEventLater + '"]');
        if (amount) {
          amount.hidden = !visible;
          amount.textContent = visible ? formatMoney(ordinary.total) : '';
          amount.setAttribute('aria-label', visible ? labels.eventLater.replace('__amount__', formatMoney(ordinary.total)) : '');
        }
      });

      offer.querySelectorAll('[data-dv-event-saved]').forEach(element => {
        const pairOption = element.dataset.dvEventSaved === '2';
        const ordinary = pairOption ? ordinaryPair?.total : ordinarySingle?.total;
        const current = pairOption ? pairPrice : singlePrice;
        const difference = ordinary - current;
        const show = campaign && Number.isSafeInteger(difference) && difference > 0;
        element.hidden = !show;
        element.textContent = show ? labels.eventSaving.replace('__amount__', formatMoney(difference)) : '';
      });
      offer.querySelectorAll('input[type="radio"][data-price]').forEach(radio => {
        const price = radio.value === '2' ? pairPrice : singlePrice;
        radio.dataset.price = Number.isFinite(price) ? String(price / 100) : '';
        const priceLabel = radio.closest('.dv-bundle').querySelector('.dv-bundle-price');
        priceLabel.textContent = formatMoney(price);
      });
      const saving = offer.querySelector('[data-dv-saving]');
      const hasSaving = Boolean(pair?.saving && pairPrice < originalPairPrice);
      if (saving) saving.hidden = !hasSaving;
      if (saving) saving.textContent = hasSaving ? (labels.shortSaving || labels.saving).replace('__amount__', formatMoney(originalPairPrice - pairPrice)) : '';
      if (hasSaving && saving) saving.setAttribute('aria-label', labels.saving.replace('__amount__', formatMoney(originalPairPrice - pairPrice)));
      else if (saving) saving.removeAttribute('aria-label');
      const comparison = offer.querySelector('[data-dv-compare-price]');
      comparison.hidden = !hasSaving;
      comparison.textContent = hasSaving ? formatMoney(originalPairPrice) : '';
      offer.querySelector('[data-dv-price-arrow]').hidden = !hasSaving;
      offer.querySelector('[data-dv-best]').hidden = !hasSaving;
      const pricingNote = offer.querySelector('[data-dv-pricing-note]');
      const selectedHasSaving = quantity === 2 && hasSaving;
      pricingNote.hidden = Number.isFinite(selectedPrice) && data.preview !== true && (!selectedHasSaving || offer.dataset.dvPricingVerified === 'true');
      pricingNote.textContent = data.preview === true ? labels.previewOnly : !Number.isFinite(selectedPrice) ? labels.pricingUnavailable : selectedHasSaving && offer.dataset.dvPricingVerified !== 'true' ? labels.plannedBundle : labels.previewPrices;
      submitButton.disabled = submitButton.disabled || !Number.isFinite(selectedPrice) || bonusUnavailable;
      checkoutButtons.forEach(button => { button.disabled = submitButton.disabled; });
      offer.dataset.dvBonusSelected = String(bonusSelected);
      offer.dataset.dvBonusBlocked = String(bonusUnavailable);
      offer.dataset.dvPriceCurrency = data.currency;
      offer.dataset.dvPriceAvailable = String(Number.isFinite(selectedPrice));
      mixControl.hidden = quantity !== 2;
      offer.querySelector('[data-dv-double-note]').textContent = mixed ? labels.mixedNote : labels.identicalNote;
      offer.querySelector('[data-dv-mix-intro]').hidden = !mixed;
      document.querySelectorAll('[data-dv-sticky-price]').forEach(element => { element.textContent = formatMoney(selectedPrice); });
      document.querySelectorAll('[data-dv-sticky-label]').forEach(element => { element.textContent = bonusSelected ? bonusData.withMask : checked.dataset.label; });
      const stickyHasSaving = quantity === 2 && !bonusSelected && hasSaving && Number.isFinite(selectedPrice);
      const submitValues = offer.querySelector('[data-dv-submit-values]');
      if (submitValues) {
        submitValues.hidden = !Number.isFinite(selectedPrice);
        submitValues.querySelector('[data-dv-submit-price]').textContent = formatMoney(selectedPrice);
        const submitComparison = submitValues.querySelector('[data-dv-submit-compare]');
        submitComparison.hidden = !stickyHasSaving;
        submitComparison.textContent = stickyHasSaving ? formatMoney(originalPairPrice) : '';
      }
      document.querySelectorAll('[data-dv-sticky-compare]').forEach(element => {
        element.hidden = !stickyHasSaving;
        element.textContent = stickyHasSaving ? formatMoney(originalPairPrice) : '';
      });
      document.querySelectorAll('[data-dv-sticky-saving]').forEach(element => {
        element.hidden = !stickyHasSaving;
        element.textContent = stickyHasSaving ? labels.stickySaving.replace('__amount__', formatMoney(originalPairPrice - selectedPrice)) : '';
        if (stickyHasSaving) element.setAttribute('aria-label', labels.saving.replace('__amount__', formatMoney(originalPairPrice - selectedPrice)));
        else element.removeAttribute('aria-label');
      });
      const active = states[mixed && activePanel === 1 ? 1 : 0];
      renderImage(data.variants.find(variant => variant.colour === active.colour && variant.size === active.size) || variants.find(variant => variant.colour === active.colour));
    }
    let campaignWasActive = eventConfig?.enabled && Date.now() < Date.parse(eventConfig.endsAt);
    const refreshEventClock = () => {
      const end = Date.parse(eventConfig?.endsAt);
      const now = Date.now();
      const active = Boolean(eventConfig?.enabled && Number.isFinite(end) && now < end);
      if (active !== campaignWasActive) {
        campaignWasActive = active;
        update();
        const drawer = document.querySelector('dvalia-cart');
        if (!active && typeof drawer?.refresh === 'function') drawer.refresh().catch(() => {});
      }
      const clock = offer.querySelector('[data-dv-event-clock]');
      if (!clock || !active) return;
      const secs = Math.max(0, Math.ceil((end - now) / 1000));
      const parts = [Math.floor(secs/86400), Math.floor(secs/3600)%24, Math.floor(secs/60)%60, secs%60];
      clock.textContent = parts.map(n => String(n).padStart(2,'0')).join(' : ');
    };
    if (eventConfig?.enabled) {
      const clockInterval = setInterval(() => {
        if (!offer.isConnected) { clearInterval(clockInterval); return; }
        refreshEventClock();
      }, 1000);
      document.addEventListener('visibilitychange', refreshEventClock);
      refreshEventClock();
    }
    offer.addEventListener('submit', async event => {
      event.preventDefault();
      if (submitting || data.preview === true || offer.dataset.dvSalesLocked !== 'false' || offer.dataset.dvPricingVerified !== 'true' || offer.dataset.dvPriceAvailable !== 'true') return;
      if (offer.dataset.dvBonusBlocked === 'true') return;
      const checkout = checkoutButtons.includes(event.submitter);
      const quantity = Number(quantityInput.value);
      const selections = dvaliaSelections(states, quantity, quantity === 2 && mixToggle.checked);
      const items = dvaliaCartItems(variants, selections);
      if (!items) { render(); return; }
      if (quantity === 2 && bonusCheckbox?.checked) {
        const mask = bonusData?.variants.find(variant => String(variant.id) === bonusChoice?.value);
        if (bonusData?.verified !== true || !mask?.available || mask.stock < 1 || !Number.isSafeInteger(Number(mask.id))) { render(); return; }
        items.push({ id: Number(mask.id), quantity: 1 });
      }
      const button = submitButton;
      submitting = true;
      button.disabled = true;
      checkoutButtons.forEach(control => { control.disabled = true; control.setAttribute('aria-busy', 'true'); });
      try {
        const drawer = document.querySelector('dvalia-cart');
        if (!checkout && drawer && typeof drawer.addItems === 'function') {
          await drawer.addItems(items);
          return;
        }
        const response = await fetch(offer.action + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || labels.unavailable);
        }
        if (checkout) {
          const checkoutUrl = new URL(offer.action, window.location.origin);
          checkoutUrl.pathname = checkoutUrl.pathname.replace(/\/cart\/add$/, '/checkout');
          checkoutUrl.search = '';
          window.location.assign(checkoutUrl.href);
          return;
        }
        window.location.assign(offer.action.replace(/\/add$/, ''));
      } catch (error) {
        status.hidden = false;
        status.textContent = error.message || labels.unavailable;
      } finally {
        submitting = false;
        button.disabled = data.preview === true || !items || offer.dataset.dvSalesLocked !== 'false' || offer.dataset.dvPricingVerified !== 'true' || offer.dataset.dvPriceAvailable !== 'true' || offer.dataset.dvBonusBlocked === 'true';
        checkoutButtons.forEach(control => { control.disabled = button.disabled; control.removeAttribute('aria-busy'); });
      }
    });
    offer.addEventListener('change', event => {
      const revealRequest = ++colourReveal;
      if (event.target === mixToggle && !mixToggle.checked) activePanel = 0;
      if (event.target.matches('input[type="radio"][data-price]') && event.target.value === '1') {
        mixToggle.checked = false;
        activePanel = 0;
      }
      const panel = event.target.closest('[data-dv-unit]');
      if (panel) {
        activePanel = panels.indexOf(panel);
        const state = states[activePanel];
        if (event.target.matches('[data-dv-colour]')) {
          state.colour = event.target.value;
          if (state.size && !dvaliaVariantFor(variants, state.colour, state.size)) state.size = '';
        } else if (event.target.matches('[data-dv-size]')) state.size = event.target.value;
      }
      if (!mixToggle.checked) {
        states[1].colour = states[0].colour;
        states[1].size = states[0].size;
      }
      render();
      if (panel && event.target.matches('[data-dv-colour]')) void revealColourImage(revealRequest);
    });
    render();
  });
}
hydrateDvaliaGalleries();
hydrateDvaliaOffers();
document.addEventListener('shopify:section:load', event => { hydrateDvaliaGalleries(event.target); hydrateDvaliaOffers(event.target); });
document.addEventListener('click', event => {
  const link = event.target.closest('.dv-mobile-menu a');
  if (link) link.closest('details').open = false;
});
document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return;
  const menu = document.querySelector('.dv-mobile-menu[open]');
  if (!menu) return;
  menu.open = false;
  menu.querySelector('summary').focus();
});









